/* eslint-disable no-duplicate-imports */
import type React from "react";
import { useEffect, useRef, useState, type ReactNode } from "react";
import {
	getTopicSubscription,
	setTopicSubscription,
} from "../utils/storage/topicStorage";
import {
	StorageContext,
	type StorageContextValue,
	type StorageState,
} from "./StorageContext";
import {
	type CourseProgress,
	getCourseProgress,
	setCourseProgress,
} from "@/utils/storage/courseProgressStorage";
import type { VideoProgress } from "@/utils/storage/videoProgressStorage";
import { getVideoProgress, setVideoProgress } from "@/utils/storage/videoProgressStorage";

interface StorageProviderProps {
	children: ReactNode;
}

export const StorageProvider: React.FC<StorageProviderProps> = ({
	children,
}) => {
	const [storageState, setStorageState] = useState<StorageState>({
		topicSubscription: null,
		courseProgress: {},
		videoProgress: {},
		isLoading: true,
	});


  const lastSaveTimeRef = useRef<number>(0);

	// Load storage data on mount
	useEffect(() => {
		const loadStorageData = async (): Promise<void> => {
			try {
				const [topicSubscription, courseProgress, videoProgress] = await Promise.all([
					getTopicSubscription(),
					getCourseProgress(),
					getVideoProgress(),
				]);

				setStorageState({
					topicSubscription,
					courseProgress,
					videoProgress,
					isLoading: false,
				});
			} catch (error) {
				console.error("Error loading storage data:", error);
				setStorageState({
					topicSubscription: null,
					courseProgress: {},
					videoProgress: {},
					isLoading: false,
				});
			}
		};

		loadStorageData().catch((error) => {
			console.error("Error loading storage data:", error);
		});
	}, []);

	const setTopicSubscriptionValue = async (value: boolean): Promise<void> => {
		try {
			await setTopicSubscription(value);
			setStorageState((previous) => ({
				...previous,
				topicSubscription: value,
			}));
		} catch (error) {
			console.error("Error setting topic subscription:", error);
			throw error;
		}
	};

	const setCourseProgressValue = async (
		progress: CourseProgress
	): Promise<void> => {
		try {
			await setCourseProgress(progress);
			setStorageState((previous) => ({
				...previous,
				courseProgress: progress,
			}));
		} catch (error) {
			console.error("Error setting course progress:", error);
			throw error;
		}
	};

	const markCourseAsCompleted = async (
		workshopId: string,
		courseClassesId: string
	): Promise<void> => {
		const updatedProgress = {
			...storageState.courseProgress,
			[workshopId]: {
				...storageState.courseProgress[workshopId],
				[courseClassesId]: true,
			},
		};
		await setCourseProgressValue(updatedProgress);
	};

	const markCourseAsIncomplete = async (
		workshopId: string,
		courseClassesId: string
	): Promise<void> => {
		const updatedProgress = {
			...storageState.courseProgress,
			[workshopId]: {
				...storageState.courseProgress[workshopId],
				[courseClassesId]: false,
			},
		};
		await setCourseProgressValue(updatedProgress);
	};

	const isCourseCompleted = (
		workshopId: string,
		courseClassesId: string
	): boolean => {
		return storageState.courseProgress[workshopId]?.[courseClassesId] || false;
	};

	const getWorkshopProgress = (
		workshopId: string
	): { [courseClassesId: string]: boolean } => {
		return storageState.courseProgress[workshopId] || {};
	};

  const setVideoProgressValue = async (progress: VideoProgress): Promise<void> => {
		try {
			await setVideoProgress(progress);
			setStorageState((previous) => ({
				...previous,
				videoProgress: progress,
			}));
		} catch (error) {
			console.error("Error setting video progress:", error);
			throw error;
		}
	};

	const saveVideoProgressInternal = async (
		workshopId: string,
		courseClassesId: string,
		currentTime: number,
		duration: number
	): Promise<void> => {
		const now = Date.now();

    const SAVE_INTERVAL = 5000;
		// Only save if 5 seconds have passed since last save
		if (now - lastSaveTimeRef.current < SAVE_INTERVAL) {
			return;
		}

		lastSaveTimeRef.current = now;

		const updatedProgress = {
			...storageState.videoProgress,
			[workshopId]: {
				...storageState.videoProgress[workshopId],
				[courseClassesId]: {
					currentTime,
					duration,
					lastSaved: now,
				},
			},
		};

		await setVideoProgressValue(updatedProgress);
		console.log(
			`💾 Video progress saved: ${Math.round(currentTime)}s / ${Math.round(duration)}s`
		);
	};

	const getVideoProgressForCourse = (
		workshopId: string,
		courseClassesId: string
	): { currentTime: number; duration: number; lastSaved: number } | null => {
		return storageState.videoProgress[workshopId]?.[courseClassesId] || null;
	};

	const clearVideoProgressForCourse = async (
		workshopId: string,
		courseClassesId: string
	): Promise<void> => {
		const updatedProgress = { ...storageState.videoProgress };
		
		if (updatedProgress[workshopId]?.[courseClassesId]) {
			delete updatedProgress[workshopId][courseClassesId];

			// If workshop has no more videos, remove the workshop key
			if (Object.keys(updatedProgress[workshopId]).length === 0) {
				delete updatedProgress[workshopId];
			}

			await setVideoProgressValue(updatedProgress);
		}
	};

	const shouldResumeFromSavedPosition = (
		workshopId: string,
		courseClassesId: string,
		videoDuration: number
	): number | null => {
		const saved = getVideoProgressForCourse(workshopId, courseClassesId);

		if (!saved) return null;

		// Don't resume if we're near the end (last 30 seconds or 95% complete)
		const isNearEnd =
			saved.currentTime > videoDuration - 30 ||
			saved.currentTime / videoDuration > 0.95;

		if (isNearEnd) return null;

		// Don't resume if saved time is too old (more than 7 days)
		const sevenDaysInMs = 7 * 24 * 60 * 60 * 1000;
		if (Date.now() - saved.lastSaved > sevenDaysInMs) return null;

		// Only resume if we're at least 10 seconds in
		return saved.currentTime > 10 ? saved.currentTime : null;
	};

	const refreshStorageState = async (): Promise<void> => {
		try {
			const topicSubscription = await getTopicSubscription();

			setStorageState((previous) => ({
				...previous,
				topicSubscription,
			}));
		} catch (error) {
			console.error("Error refreshing storage state:", error);
		}
	};

	const contextValue: StorageContextValue = {
		...storageState,
		setTopicSubscriptionValue,
		setCourseProgressValue,
		markCourseAsCompleted,
		markCourseAsIncomplete,
		isCourseCompleted,
		getWorkshopProgress,
		setVideoProgressValue,
		saveVideoProgress: saveVideoProgressInternal,
		getVideoProgressForCourse,
		clearVideoProgressForCourse,
		shouldResumeFromSavedPosition,
		refreshStorageState,
	};

	return (
		<StorageContext.Provider value={contextValue}>
			{children}
		</StorageContext.Provider>
	);
};
